/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * TanStack Query hooks for Submissions
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { authenticatedApi, unwrapResponse, uploadFile } from "@/api/authenticatedClient";
import { invalidateSubmissionDerived } from "@/api/cacheInvalidation";
import type {
  ApiResponse,
  PaginatedData,
  Submission,
  SubmissionListItem,
  SubmissionDetail,
  SubmissionFilters,
  CreateSubmissionInput,
  SubmissionFile,
  MeasureResult,
  StatusHistoryItem,
} from "@/lib/types";

// ============================================================================
// Query Keys
// ============================================================================

export const submissionKeys = {
  all: ["submissions"] as const,
  lists: () => [...submissionKeys.all, "list"] as const,
  list: (filters: SubmissionFilters) => [...submissionKeys.lists(), filters] as const,
  details: () => [...submissionKeys.all, "detail"] as const,
  detail: (id: string) => [...submissionKeys.details(), id] as const,
  files: (submissionId: string) => [...submissionKeys.detail(submissionId), "files"] as const,
  measures: (submissionId: string) => [...submissionKeys.detail(submissionId), "measures"] as const,
  measureSummary: (submissionId: string) =>
    [...submissionKeys.detail(submissionId), "measure-results", "summary"] as const,
  measureDetail: (submissionId: string, measureId: string) =>
    [...submissionKeys.detail(submissionId), "measure-results", "detail", measureId] as const,
  denominator: (submissionId: string) =>
    [...submissionKeys.detail(submissionId), "denominator"] as const,
  timeline: (submissionId: string) => [...submissionKeys.detail(submissionId), "timeline"] as const,
};

// ============================================================================
// Response Transformers
// ============================================================================

/**
 * Backend submission item (snake_case)
 */
interface BackendSubmissionItem {
  id: string;
  state_code: string;
  reporting_period: string;
  environment: string;
  status: string;
  quality_score: number | null;
  submitted_by?: string;
  submitted_at: string;
  completed_at?: string | null;
  file_count: number;
  error_count: number;
  created_at: string;
  updated_at: string;
  /** CMS spec version from ref.cms_spec_versions (available after migration 057). */
  cms_spec_version?: string | null;
}

/**
 * Backend paginated response structure
 */
interface BackendPaginatedResponse {
  items: BackendSubmissionItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface MeasureStatusCounts {
  passed: number;
  failed: number;
  skipped: number;
  no_data: number;
  flagged: number;
  total: number;
}

/** Normalized status strings from API (unknown values coerced to ``skipped`` server-side). */
export type RunHistoryRowStatus =
  "passed" | "failed" | "skipped" | "no_data" | "completed" | "error" | "flagged" | "running";

/** Per-run history entry (enhanced measure results). */
export interface RunHistoryEntry {
  run_rank: number;
  run_id: string;
  executed_at: string;
  status: RunHistoryRowStatus;
  mvalue: number | null;
  numer: number | null;
  denom: number | null;
  threshold_lower: number | null;
  threshold_upper: number | null;
  /** Threshold band for this run (aligns with row-level out_of_range_flag). */
  out_of_range_flag: string;
}

export interface MeasureSummaryItem {
  measure_id: string;
  measure_code: string;
  /** CMS T-MSIS Data Guide id (e.g. EL-3-027-36): user-facing id + deep-link slug source. */
  content_area_measure_id_with_display_order?: string | null;
  measure_name: string;
  measure_description: string | null;
  file_type: string;
  priority: "Critical" | "High" | "Medium" | "Low" | null;
  oba_category: string | null;
  claim_category: string | null;
  is_tpi: boolean | null;
  submodule: string | null;
  display_numer: number;
  display_denom: number;
  display_mvalue: number | null;
  threshold_lower: number | null;
  threshold_upper: number | null;
  total_errant_count: number;
  has_drill_down: boolean;
  breakdown_type: "NONE" | "VALID_VALUE" | "PLAN_ID" | "WAIVER_TYPE" | "PLAN_TYPE_EL";
  result_count: number;
  original_status: string;
  display_status: "PASSED" | "FAILED" | "SKIPPED" | "NO_DATA" | "FLAGGED" | "ERROR";
  /** @deprecated Use last_5_runs; no longer populated. */
  history_status: boolean[];
  /** WITHIN RANGE, BELOW LOWER THRESHOLD, ABOVE UPPER THRESHOLD, NO THRESHOLD, NO DATA */
  out_of_range_flag?: string | null;
  last_5_total_runs?: number | null;
  last_5_failed_count?: number | null;
  last_5_passed_count?: number | null;
  last_5_out_of_range_count?: number | null;
  last_run_mvalue?: number | null;
  mvalue_delta_vs_prev?: number | null;
  /** INCREASING | DECREASING | STABLE | N/A */
  mvalue_trend?: string | null;
  last_5_runs?: RunHistoryEntry[];
  /** CMS spec version this measure result was produced under (from dq_execution.measure_results). */
  cms_spec_version?: string | null;

  /** 5-month calendar trend (from fn_measure_summary) */
  trend_status?: string | null;
  trend_month_5_oldest?: string | null;
  trend_month_4?: string | null;
  trend_month_3?: string | null;
  trend_month_2?: string | null;
  trend_month_1_latest?: string | null;
  trend_m5_status?: string | null;
  trend_m4_status?: string | null;
  trend_m3_status?: string | null;
  trend_m2_status?: string | null;
  trend_m1_status?: string | null;
  trend_months_with_data?: number | null;

  /** Errant summary (from fn_errant_summary LEFT JOIN) */
  errant_total?: number | null;
  errant_file_types?: string | null;
  has_errant_records?: boolean | null;
  representative_result_id?: string | null;
}

export interface MeasureSummaryResponse {
  items: MeasureSummaryItem[];
  status_counts: MeasureStatusCounts;
  total: number;
}

export interface DrillDownRow {
  id: string;
  label: string;
  breakdown_type: "NONE" | "VALID_VALUE" | "PLAN_ID" | "WAIVER_TYPE" | "PLAN_TYPE_EL";
  plan_id: string | null;
  valid_value: string | null;
  waiver_type: string | null;
  plan_type_el: string | null;
  numer: number;
  denom: number;
  mvalue: number;
  is_no_data: boolean;
  claim_type: string | null;
  errant_count: number;
  threshold_lower: number | null;
  threshold_upper: number | null;
  derived_valid_count: number | null;
  is_summary_row: boolean;
  check_result: string | null;
  original_status: string;
  /** WITHIN RANGE, BELOW/ABOVE THRESHOLD, NO THRESHOLD, NO DATA, N/A */
  out_of_range_flag?: string | null;
  threshold_lower_measure?: number | null;
  threshold_upper_measure?: number | null;

  /** Errant records per drilldown row (from fn_errant_by_result) */
  errant_file_types?: string | null;
  has_errant_records?: boolean | null;
}

export interface MeasureDetailResponse {
  measure_id: string;
  measure_code: string;
  measure_name: string;
  breakdown_type: "NONE" | "VALID_VALUE" | "PLAN_ID" | "WAIVER_TYPE" | "PLAN_TYPE_EL";
  rows: DrillDownRow[];
  /** CMS spec version from dq_execution.measure_results.cms_spec_version. */
  cms_spec_version?: string | null;
  last_5_total_runs?: number | null;
  last_5_failed_count?: number | null;
  last_5_passed_count?: number | null;
  last_5_out_of_range_count?: number | null;
  last_run_mvalue?: number | null;
  mvalue_delta_vs_prev?: number | null;
  mvalue_trend?: string | null;
  last_5_runs?: RunHistoryEntry[];
}

/**
 * Format reporting period for display (e.g., "2024-01" -> "Jan 2024")
 */
function formatPeriodDisplay(reportingPeriod: string): string {
  const parts = reportingPeriod.split("-");
  const year = parts[0] || reportingPeriod;
  const month = parts[1];
  if (!month) return year;

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthIndex = parseInt(month, 10) - 1;
  return `${monthNames[monthIndex] || month} ${year}`;
}

/**
 * Generate ticket number from state code and reporting period
 */
function generateTicketNumber(stateCode: string, reportingPeriod: string, id: string): string {
  // Format: P-{STATE}-{YYYYMM}-{SHORT_ID}
  const period = reportingPeriod.replace("-", "");
  const shortId = id.slice(0, 2).toUpperCase();
  return `P-${stateCode}-${period}-${shortId}`;
}

/**
 * Transform backend submission item to frontend format (SubmissionListItem is snake_case)
 */
function transformSubmissionItem(item: BackendSubmissionItem): SubmissionListItem {
  const backend = item as BackendSubmissionItem & {
    ticket_number?: string;
    total_records?: number;
    ticket_id?: string | null;
  };
  return {
    id: item.id,
    ticket_id: backend.ticket_id ?? null,
    ticket_number:
      backend.ticket_number ??
      generateTicketNumber(item.state_code, item.reporting_period, item.id),
    state_code: item.state_code,
    reporting_period: item.reporting_period,
    period_display: formatPeriodDisplay(item.reporting_period),
    environment:
      item.environment === "P"
        ? "production"
        : item.environment === "T"
          ? "test"
          : item.environment,
    status: item.status as SubmissionListItem["status"],
    quality_score: item.quality_score,
    total_records: backend.total_records ?? item.file_count,
    submitted_at: item.submitted_at,
    created_at: item.created_at,
    file_count: item.file_count,
    error_count: item.error_count,
    cms_spec_version: item.cms_spec_version ?? null,
  };
}

/**
 * Transform backend paginated response to frontend format
 */
function transformPaginatedResponse(
  response: ApiResponse<BackendPaginatedResponse>
): PaginatedData<SubmissionListItem> {
  if (!response.success || !response.data) {
    return {
      data: [],
      pagination: {
        total: 0,
        page: 1,
        pageSize: 20,
        totalPages: 0,
      },
    };
  }

  const backendData = response.data;
  return {
    data: backendData.items.map(transformSubmissionItem),
    pagination: {
      total: backendData.total,
      page: backendData.page,
      pageSize: backendData.page_size,
      totalPages: backendData.total_pages,
    },
  };
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch paginated list of submissions
 */
export function useSubmissions(filters: SubmissionFilters = {}) {
  return useQuery({
    queryKey: submissionKeys.list(filters),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<BackendPaginatedResponse>>(
        "/submissions/",
        {
          params: {
            search: filters.search,
            state_code: filters.stateCode,
            environment: filters.environment,
            status: filters.status?.join(","),
            date_from: filters.dateFrom,
            date_to: filters.dateTo,
            page: filters.page,
            page_size: filters.pageSize,
            sort_by: filters.sortBy,
            sort_order: filters.sortOrder,
          },
        }
      );
      return transformPaginatedResponse(response);
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Fetch infinite list of submissions (for virtual scrolling)
 */
export function useInfiniteSubmissions(filters: Omit<SubmissionFilters, "page">) {
  return useInfiniteQuery({
    queryKey: [...submissionKeys.lists(), "infinite", filters],
    queryFn: async ({ pageParam }) => {
      const response = await authenticatedApi.get<ApiResponse<BackendPaginatedResponse>>(
        "/submissions/",
        {
          params: {
            search: filters.search,
            state_code: filters.stateCode,
            environment: filters.environment,
            status: filters.status?.join(","),
            date_from: filters.dateFrom,
            date_to: filters.dateTo,
            page: pageParam,
            page_size: filters.pageSize || 50,
            sort_by: filters.sortBy,
            sort_order: filters.sortOrder,
          },
        }
      );
      return transformPaginatedResponse(response);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch submission detail by ID
 */
export function useSubmission(id: string | undefined) {
  return useQuery({
    queryKey: submissionKeys.detail(id || ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<SubmissionDetail>>(
        `/submissions/${id ?? ""}`
      );
      return unwrapResponse(response);
    },
    enabled: !!id,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Fetch submission files
 */
export function useSubmissionFiles(submissionId: string | undefined) {
  return useQuery({
    queryKey: submissionKeys.files(submissionId || ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<SubmissionFile[]>>(
        `/submissions/${submissionId ?? ""}/files`
      );
      return unwrapResponse(response);
    },
    enabled: !!submissionId,
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch submission measure results
 */
export function useSubmissionMeasures(submissionId: string | undefined) {
  return useQuery({
    queryKey: submissionKeys.measures(submissionId || ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<MeasureResult[]>>(
        `/submissions/${submissionId ?? ""}/measures`
      );
      return unwrapResponse(response);
    },
    enabled: !!submissionId,
    staleTime: 60 * 1000,
  });
}

export function useSubmissionMeasureSummary(submissionId: string | undefined) {
  return useQuery({
    queryKey: submissionKeys.measureSummary(submissionId || ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<MeasureSummaryResponse>>(
        `/submissions/${submissionId ?? ""}/measure-results/summary`
      );
      return unwrapResponse(response);
    },
    enabled: !!submissionId,
    staleTime: 60 * 1000,
  });
}

export function useSubmissionMeasureDetail(
  submissionId: string | undefined,
  measureId: string | null | undefined
) {
  return useQuery({
    queryKey: submissionKeys.measureDetail(submissionId || "", measureId || ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<MeasureDetailResponse>>(
        `/submissions/${submissionId ?? ""}/measure-results/${encodeURIComponent(
          measureId ?? ""
        )}/detail`
      );
      return unwrapResponse(response);
    },
    enabled: !!submissionId && !!measureId,
    staleTime: 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// CMS-64 denominator — dq_execution.cms64_denominator (Denominator popup, FFY submissions)
// ---------------------------------------------------------------------------

export interface DenominatorItem {
  measure_id: string;
  measure_code: string;
  /** CMS T-MSIS Data Guide id (e.g. EXP-47-001-1) — user-facing id, falls back to measure_code. */
  content_area_measure_id_with_display_order?: string | null;
  measure_name: string;
  submtg_state_cd: string;
  cms_spec_version: string;
  ffy: number;
  total_computable: number;
  n_rows_summed: number | null;
  rows_summed: string | null;
}

export interface DenominatorResponse {
  items: DenominatorItem[];
  /** Every FFY column to show, ascending — the submission's own FFY plus the prior year. */
  ffys: number[];
  /** Deprecated: the latest FFY in `ffys` (kept for backward compatibility). */
  ffy: number | null;
  total: number;
}

/**
 * CMS-64 denominator rows for the submission's FFY. `enabled` lets callers defer the fetch until
 * the popup actually opens.
 */
export function useSubmissionDenominator(submissionId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: submissionKeys.denominator(submissionId || ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<DenominatorResponse>>(
        `/submissions/${submissionId ?? ""}/denominator`
      );
      return unwrapResponse(response);
    },
    enabled: !!submissionId && enabled,
    staleTime: 60 * 1000,
  });
}

// CMS-64 FFY reconciliation math for an EXP measure (measure detail modal, FFY submissions)
// ---------------------------------------------------------------------------

/** CMS-64 expenditure calculation for one EXP measure (lines 3, 13a-16). Sourced from plan_extended_data. */
export interface Cms64FfyBreakdown {
  denominator: number; // (3)
  tmsis_ffy_current: number; // (13a)
  tmsis_ffy_prior: number; // (13b)
  net_ffy: number; // (14) = 13a - 13b
  numerator: number; // (15) = denominator - net
  percentage: number; // (16) = numerator / denominator
  min: number;
  max: number;
  flag: string;
  ffy_current: number | null;
  ffy_prior: number | null;
}

/**
 * CMS-64 FFY reconciliation math for one EXP measure. Returns `null` when the measure has no such
 * breakdown (non-EXP measures / monthly submissions). `enabled` defers the fetch until the modal
 * opens.
 */
export function useMeasureFfyBreakdown(
  submissionId: string | undefined,
  measureCode: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: [...submissionKeys.detail(submissionId || ""), "ffy-breakdown", measureCode || ""],
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<Cms64FfyBreakdown | null>>(
        `/submissions/${submissionId ?? ""}/measure-results/${encodeURIComponent(
          measureCode ?? ""
        )}/ffy-breakdown`
      );
      return unwrapResponse(response);
    },
    enabled: !!submissionId && !!measureCode && enabled,
    staleTime: 60 * 1000,
  });
}

/** One EXP measure's CMS-64 FFY reconciliation, tagged with its code/name (for the export tab). */
export interface Cms64FfyBreakdownRow extends Cms64FfyBreakdown {
  measure_code: string;
  /** CMS T-MSIS Data Guide id (e.g. EXP-46-001-1) — user-facing id, falls back to measure_code. */
  content_area_measure_id_with_display_order?: string | null;
  measure_name: string | null;
}

/**
 * CMS-64 FFY reconciliation for every EXP measure in a submission — one row per measure. Powers the
 * "CMS-64 Reconciliation" tab in the export workbook. Returns an empty array for monthly
 * submissions or submissions with no EXP/FFY breakdowns. `enabled` defers the fetch.
 */
export function useMeasureFfyBreakdowns(submissionId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...submissionKeys.detail(submissionId || ""), "ffy-breakdowns"],
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<Cms64FfyBreakdownRow[]>>(
        `/submissions/${submissionId ?? ""}/measure-results/ffy-breakdowns`
      );
      return unwrapResponse(response);
    },
    enabled: !!submissionId && enabled,
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch submission status timeline
 */
export function useSubmissionTimeline(submissionId: string | undefined) {
  return useQuery({
    queryKey: submissionKeys.timeline(submissionId || ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<StatusHistoryItem[]>>(
        `/submissions/${submissionId ?? ""}/timeline`
      );
      return unwrapResponse(response);
    },
    enabled: !!submissionId,
    staleTime: 60 * 1000,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new submission
 */
export function useCreateSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateSubmissionInput) => {
      const response = await authenticatedApi.post<ApiResponse<Submission>>("/submissions/", input);
      return unwrapResponse(response);
    },
    onSuccess: () => {
      // Refresh both list namespaces + dashboard/analytics read models (a new submission changes
      // dashboard KPIs). Covers the old submissions-list-namespace gap too.
      invalidateSubmissionDerived(queryClient);
    },
  });
}

/**
 * Update a submission
 */
export function useUpdateSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateSubmissionInput> }) => {
      const response = await authenticatedApi.patch<ApiResponse<Submission>>(
        `/submissions/${id}`,
        data
      );
      return unwrapResponse(response);
    },
    onSuccess: (_, { id }) => {
      invalidateSubmissionDerived(queryClient, id);
    },
  });
}

/**
 * Delete a submission
 *
 * ``submissionKeys`` and ``submissionsListKeys`` are two independent
 * cache root keys (``["submissions"]`` vs ``["submissions-list"]``).
 * The Submissions list page + stats cards subscribe to the latter via
 * ``useSubmissionsList`` / ``useSubmissionStats``, so we must invalidate
 * both roots -- otherwise the list stays stale after a delete and the
 * user has to reload the page manually.
 */
export function useDeleteSubmission() {
  const queryClient = useQueryClient();

  // The `string` TVariables generic is the actual constraint on `mutate(...)` — a caller passing an
  // object is a compile error at the call site. (An object would satisfy invalidateSubmissionDerived's
  // truthy id guard yet never match a real query key, silently skipping detail invalidation.)
  return useMutation<ApiResponse<null>, Error, string>({
    mutationFn: async (id) => {
      const response = await authenticatedApi.delete<ApiResponse<null>>(`/submissions/${id}`);
      if (!response.success) {
        throw new Error(response.message || "Failed to delete submission");
      }
      return response;
    },
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: submissionKeys.all });
      // Both list namespaces + dashboard/analytics; the detail subtree for the deleted id.
      invalidateSubmissionDerived(queryClient, id);
    },
  });
}

/**
 * Upload a file to a submission
 */
export function useUploadFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      submissionId,
      file,
      onProgress,
    }: {
      submissionId: string;
      file: File;
      onProgress?: (progress: number) => void;
    }) => {
      const response = await uploadFile(`/submissions/${submissionId}/files`, file, { onProgress });
      return unwrapResponse(response);
    },
    onSuccess: (_, { submissionId }) => {
      invalidateSubmissionDerived(queryClient, submissionId);
    },
  });
}

/**
 * Cancel a submission
 */
export function useCancelSubmission() {
  const queryClient = useQueryClient();

  // `string` TVariables generic constrains mutate(...) to a bare id (see useDeleteSubmission).
  return useMutation<Submission, Error, string>({
    mutationFn: async (id) => {
      const response = await authenticatedApi.post<ApiResponse<Submission>>(
        `/submissions/${id}/cancel`
      );
      return unwrapResponse(response);
    },
    onSuccess: (_, id) => {
      invalidateSubmissionDerived(queryClient, id);
    },
  });
}

/**
 * Retry a failed submission
 */
export function useRetrySubmission() {
  const queryClient = useQueryClient();

  // `string` TVariables generic constrains mutate(...) to a bare id (see useDeleteSubmission).
  return useMutation<Submission, Error, string>({
    mutationFn: async (id) => {
      const response = await authenticatedApi.post<ApiResponse<Submission>>(
        `/submissions/${id}/retry`
      );
      return unwrapResponse(response);
    },
    onSuccess: (_, id) => {
      invalidateSubmissionDerived(queryClient, id);
    },
  });
}

// ============================================================================
// Prefetch Helpers
// ============================================================================

/**
 * Prefetch submission detail (call on row hover)
 */
export function usePrefetchSubmission() {
  const queryClient = useQueryClient();

  return (id: string) => {
    void queryClient
      .query({
        queryKey: submissionKeys.detail(id),
        queryFn: async () => {
          const response = await authenticatedApi.get<ApiResponse<SubmissionDetail>>(
            `/submissions/${id}`
          );
          return unwrapResponse(response);
        },
        staleTime: 60 * 1000,
      })
      // prefetchQuery swallowed failures; query() rejects. A hover prefetch must stay
      // silent -- if it fails, the real fetch on navigation surfaces the error properly.
      //
      // Use query(), not fetchQuery(): fetchQuery is deprecated in @tanstack/react-query 5.102
      // ("Use queryClient.query(options) instead"), and eslint's no-deprecated fails the build on
      // it. query() looks absent only when node_modules is stale on 5.101 -- run `npm ci` before
      // concluding it does not exist.
      .catch(() => {});
  };
}
